"use client";

import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb";
import {
  ShopBreadcrumbsProvider,
  useShopBreadcrumbs,
} from "@/integrations/wordpress/WordPressEcommerceShopBreadcrumbs";
import { SprayCan } from "lucide-react";

function ShopBreadcrumbsConsumer() {
  const { links, loading, error } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="flex items-center gap-2">
        <div className="h-4 w-16 rounded-md bg-muted/60 animate-pulse" />
        <span className="text-muted-foreground">/</span>
        <div className="h-4 w-24 rounded-md bg-muted/60 animate-pulse" />
        <span className="text-muted-foreground">/</span>
        <div className="h-4 w-20 rounded-md bg-muted/60 animate-pulse" />
      </div>
    );
  }

  if (error || links.length === 0) return null;

  const lastIndex = links.length - 1;

  return (
    <Breadcrumb>
      <BreadcrumbList className="text-foreground/70">
        {links.map((link, i) => (
          <BreadcrumbItem
            data-index={i}
            key={link.href}
            className="font-default"
          >
            {i === lastIndex ? (
              <BreadcrumbPage className="text-foreground font-medium">
                {link.title}
              </BreadcrumbPage>
            ) : (
              <>
                <BreadcrumbLink
                  href={link.href}
                  className="text-foreground/70 hover:text-primary transition-colors"
                >
                  {link.title}
                </BreadcrumbLink>
                <BreadcrumbSeparator className="text-foreground/40" />
              </>
            )}
          </BreadcrumbItem>
        ))}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

function ArchiveTitleHeading() {
  const { links, loading } = useShopBreadcrumbs();

  if (loading) {
    return (
      <div className="mt-6 space-y-3">
        <div className="h-12 w-2/3 rounded-md bg-muted/60 animate-pulse" />
        <div className="h-4 w-1/2 rounded-md bg-muted/50 animate-pulse" />
      </div>
    );
  }

  const currentTitle =
    links.length > 0 ? links[links.length - 1].title : "Shop";

  return (
    <div className="mt-6">
      <h1 className="font-default font-serif text-4xl sm:text-5xl lg:text-6xl tracking-tight text-foreground">
        {currentTitle}
      </h1>
      <p className="mt-4 max-w-2xl text-base sm:text-lg text-muted-foreground font-default">
        Explore our gentle, portable hygiene solutions designed for newborn families, travelers, and childcare environments. Before You Enter™, stay protected.
      </p>
    </div>
  );
}

export default function Archive() {
  return (
    <section data-section-id="451"
      id="archive-title"
      className="relative bg-background text-foreground border-b border-border overflow-hidden"
    >
      {}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-24 -right-20 h-64 w-64 rounded-lg bg-primary/5 blur-3xl"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-lg bg-secondary/20 blur-3xl"
      />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14 sm:py-20 lg:py-24">
        <ShopBreadcrumbsProvider>
          <div className="flex flex-col">
            {}
            <div className="flex items-center">
              <ShopBreadcrumbsConsumer />
            </div>

            {}
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
              <ArchiveTitleHeading />

              <div className="hidden md:flex items-center gap-3 rounded-lg bg-accent/60 px-4 py-3 border border-border">
                <span className="inline-flex h-9 w-9 items-center justify-center rounded-md bg-primary/10 text-primary">
                  <SprayCan className="h-5 w-5" />
                </span>
                <div className="flex flex-col">
                  <span className="text-xs uppercase tracking-wider text-muted-foreground font-default">
                    B.Y.E. Promise
                  </span>
                  <span className="text-sm font-medium text-foreground font-default">
                    Gentle Hygiene Mist
                  </span>
                </div>
              </div>
            </div>

            {}
            <div className="mt-10 flex items-center gap-3">
              <span className="h-px w-12 bg-primary/40" />
              <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground font-default">
                B.Y.E. Hygiene Mist™ Collection
              </span>
              <span className="h-px flex-1 bg-border" />
            </div>
          </div>
        </ShopBreadcrumbsProvider>
      </div>
    </section>
  );
}